/**
 * Shared "Updated …" date + time formatter.
 *
 * 2026-07-11 (first-touch rehearsal, finding #11): the dashboard topbar was
 * rendering the raw ISO timestamp straight from the API —
 * "UPDATED 2026-07-11T08:18:04.292Z" — instead of something a client can
 * actually read. Centralised here (rather than inlined at the one call-site)
 * so any future "Updated …" surface reuses the identical rendering.
 *
 * Mirrors the degrade-gracefully convention used by the other local
 * `formatDate` helpers in this codebase (e.g.
 * `routes/app/trusts/$id/index.tsx`): missing → 'Not on file',
 * unparseable → the original string verbatim (never a
 * fabricated epoch date), valid → a locale-stable "<day> <Mon> <year>,
 * <HH>:<MM>" in the viewer's local time (en-ZA, 24-hour clock — matches the
 * date-only formatters already used across the dashboard/trust screens).
 *
 * Usage:
 *
 *     import { formatUpdatedAt } from '../../lib/format/date-time';
 *
 *     <span>Updated {formatUpdatedAt(summary?.lastUpdated)}</span>
 */

export function formatUpdatedAt(iso: string | null | undefined): string {
  if (!iso) return 'Not on file';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const datePart = d.toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timePart = d.toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${datePart}, ${timePart}`;
}

/**
 * Compact relative-time label ("2m ago" / "in 3d") from an ISO string.
 *
 * Lives here rather than beside its first caller because there are now two
 * notification bells that need it: the client shell's and the firm console's.
 * `components/screens/screen-shell.tsx` still carries a private twin of this
 * function; fold it into this one the next time that file is open. An empty
 * string for an unparseable value, never a fabricated "just now".
 */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffMs = t - now;
  const future = diffMs > 0;
  const abs = Math.abs(diffMs);
  const min = Math.round(abs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return future ? `in ${min}m` : `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return future ? `in ${hr}h` : `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return future ? `in ${day}d` : `${day}d ago`;
  const mo = Math.round(day / 30);
  return future ? `in ${mo}mo` : `${mo}mo ago`;
}
